import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ClipboardList, Plus, Loader2, CheckCircle2, Trash2, Clock,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_META = {
  low:      { label: 'Low',      color: 'bg-slate-100 text-slate-600' },
  medium:   { label: 'Medium',   color: 'bg-sky-100 text-sky-700' },
  high:     { label: 'High',     color: 'bg-amber-100 text-amber-700' },
  critical: { label: 'Critical', color: 'bg-rose-100 text-rose-700' },
};

const STATUS_META = {
  open:         { label: 'Open',         color: 'bg-slate-100 text-slate-600' },
  in_progress:  { label: 'In Progress',  color: 'bg-sky-100 text-sky-700' },
  done:         { label: 'Done',         color: 'bg-emerald-100 text-emerald-700' },
  cancelled:    { label: 'Cancelled',    color: 'bg-slate-100 text-slate-400' },
};


const TasksList = () => {
  const { toast } = useToast();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', assignee_email: '', due_date: '', priority: 'medium',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterSource) params.set('source', filterSource);
      const r = await axios.get(`${API_URL}/api/tasks?${params.toString()}`, authHeader);
      setTasks(r.data || []);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [authHeader, filterStatus, filterSource, toast]);

  useEffect(() => { load(); }, [load]);

  const createTask = useCallback(async () => {
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/tasks`, form, authHeader);
      toast({ title: 'Task created' });
      setDialogOpen(false);
      setForm({ title: '', description: '', assignee_email: '', due_date: '', priority: 'medium' });
      await load();
    } catch (err) {
      toast({ title: 'Create failed', description: err?.message, variant: 'destructive' });
    } finally { setCreating(false); }
  }, [form, authHeader, toast, load]);

  const updateStatus = useCallback(async (id, status) => {
    try {
      await axios.patch(`${API_URL}/api/tasks/${id}`, { status }, authHeader);
      await load();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.message, variant: 'destructive' });
    }
  }, [authHeader, toast, load]);

  const deleteTask = useCallback(async (id) => {
    if (!window.confirm('Delete this task?')) return; // eslint-disable-line no-alert
    try {
      await axios.delete(`${API_URL}/api/tasks/${id}`, authHeader);
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.message, variant: 'destructive' });
    }
  }, [authHeader, toast, load]);

  return (
    <div data-testid="tasks-list-page">
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
            <ClipboardList className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#2B2D42]">Task Center</h1>
            <p className="text-sm text-[#6C757D]">Manual and automation-generated tasks in one place.</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="tasks-new-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New Task</Button>
          </DialogTrigger>
          <DialogContent data-testid="tasks-new-dialog">
            <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <Field label="Title *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-form-title" /></Field>
              <Field label="Description"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-form-description" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Assignee email"><Input value={form.assignee_email} onChange={(e) => setForm({ ...form, assignee_email: e.target.value })} data-testid="task-form-assignee" /></Field>
                <Field label="Due date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-form-due" /></Field>
              </div>
              <Field label="Priority">
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-form-priority" className="text-sm w-full border border-slate-300 rounded px-2 py-1 bg-white">
                  {Object.keys(PRIORITY_META).map((k) => (<option key={k} value={k}>{PRIORITY_META[k].label}</option>))}
                </select>
              </Field>
            </div>
            <DialogFooter>
              <Button size="sm" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={createTask} disabled={creating || !form.title} data-testid="task-form-submit">
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-4">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="tasks-filter-status" className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
            <option value="">All statuses</option>
            {Object.keys(STATUS_META).map((k) => (<option key={k} value={k}>{STATUS_META[k].label}</option>))}
          </select>
          <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} data-testid="tasks-filter-source" className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
            <option value="">All sources</option>
            <option value="manual">Manual</option>
            <option value="workflow">Workflow</option>
            <option value="system">System</option>
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 flex items-center gap-2 text-slate-500" data-testid="tasks-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : tasks.length === 0 ? (
            <div className="p-6 text-center text-xs text-[#6C757D]" data-testid="tasks-empty">No tasks match your filters.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="tasks-table">
              {tasks.map((t) => {
                const pm = PRIORITY_META[t.priority] || PRIORITY_META.medium;
                const sm = STATUS_META[t.status] || STATUS_META.open;
                return (
                  <li key={t.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`task-row-${t.id}`}>
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <input type="checkbox" checked={t.status === 'done'} onChange={(e) => updateStatus(t.id, e.target.checked ? 'done' : 'open')} data-testid={`task-check-${t.id}`} className="mt-1 w-4 h-4" />
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-semibold ${t.status === 'done' ? 'line-through text-slate-400' : 'text-[#2B2D42]'} truncate`}>{t.title}</div>
                        <div className="text-[11px] text-[#6C757D] truncate">{t.description}</div>
                        <div className="mt-1 flex items-center gap-2 text-[10px]">
                          <span className={`font-bold uppercase px-1.5 py-0.5 rounded ${sm.color}`}>{sm.label}</span>
                          <span className={`font-bold uppercase px-1.5 py-0.5 rounded ${pm.color}`}>{pm.label}</span>
                          {t.source !== 'manual' && <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-bold uppercase">{t.source}</span>}
                          {t.due_date && <span className="text-[#6C757D]"><Clock className="w-2.5 h-2.5 inline mr-0.5" />{t.due_date}</span>}
                          {t.assignee_email && <span className="text-[#6C757D]">@{t.assignee_email}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {t.status !== 'done' && (
                        <Button size="sm" variant="ghost" onClick={() => updateStatus(t.id, 'done')} data-testid={`task-done-${t.id}`} title="Mark done">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteTask(t.id)} data-testid={`task-delete-${t.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase font-semibold text-[#6C757D] block mb-1">{label}</Label>
    {children}
  </div>
);


export default TasksList;
